import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { SideMenuPageContainer } from "../../components/side_menu_page_container/SideMenuPageContainer";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import {
  hoursChanged,
  minutesChanged,
  secondsChanged,
  startTimer,
  pauseTimer,
  resumeTimer,
  stopTimer,
  resetToOriginal,
  tickTimer,
  setQuickTime,
  showNotificationChanged,
  playSoundChanged,
  clearError,
  formatTimeDisplay,
  requestNotificationPermission,
  selectState,
} from "./CountdownTimerSlice";
import {
  Box,
  Divider,
  Stack,
  TextField,
  Typography,
  Alert,
  Paper,
  Switch,
  FormControlLabel,
  Chip,
} from "@mui/material";
import { CustomButton } from "../../components/custom_button/CustomButton";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import NotificationsIcon from "@mui/icons-material/Notifications";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AdSense from "../../components/adsense/AdSense";
import { ADSENSE_SLOTS } from "../../config/adsense";

export function CountdownTimer() {
  const dispatch = useAppDispatch();
  const state = useAppSelector(selectState);
  const { t } = useTranslation();

  // Timer effect
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;

    if (state.isRunning && state.timeRemaining > 0) {
      interval = setInterval(() => {
        dispatch(tickTimer());
      }, 1000);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [state.isRunning, state.timeRemaining, dispatch]);

  // Request notification permission on component mount
  useEffect(() => {
    if (state.showNotification) {
      requestNotificationPermission();
    }
  }, [state.showNotification]);

  const handleStart = useCallback(() => {
    dispatch(startTimer());
  }, [dispatch]);

  const handlePause = useCallback(() => {
    dispatch(pauseTimer());
  }, [dispatch]);

  const handleResume = useCallback(() => {
    dispatch(resumeTimer());
  }, [dispatch]);

  const handleStop = useCallback(() => {
    dispatch(stopTimer());
  }, [dispatch]);

  const handleReset = useCallback(() => {
    dispatch(resetToOriginal());
  }, [dispatch]);

  const handleQuickTime = useCallback(
    (hours: number, minutes: number, seconds: number) => {
      dispatch(setQuickTime({ hours, minutes, seconds }));
    },
    [dispatch]
  );

  const quickTimePresets = [
    { label: t("countdownTimer.presets.m5"), hours: 0, minutes: 5, seconds: 0 },
    {
      label: t("countdownTimer.presets.m10"),
      hours: 0,
      minutes: 10,
      seconds: 0,
    },
    {
      label: t("countdownTimer.presets.m15"),
      hours: 0,
      minutes: 15,
      seconds: 0,
    },
    {
      label: t("countdownTimer.presets.m30"),
      hours: 0,
      minutes: 30,
      seconds: 0,
    },
    { label: t("countdownTimer.presets.h1"), hours: 1, minutes: 0, seconds: 0 },
    { label: t("countdownTimer.presets.h2"), hours: 2, minutes: 0, seconds: 0 },
  ];

  const getProgressPercentage = () => {
    if (state.originalTime === 0) return 0;
    return (
      ((state.originalTime - state.timeRemaining) / state.originalTime) * 100
    );
  };

  const isTimerActive = state.isRunning || state.isPaused;
  const hasTimeSet = state.hours > 0 || state.minutes > 0 || state.seconds > 0;

  return (
    <SideMenuPageContainer
      pageTitle={t("countdownTimer.title")}
      pageSeoTitle={t("countdownTimer.seoTitle")}
      pageDescription={t("countdownTimer.description")}
    >
      <Grid2 container spacing={3}>
        {/* Error Display */}
        {state.error && (
          <Grid2 xs={12}>
            <Alert
              severity="error"
              onClose={() => dispatch(clearError())}
              sx={{ mb: 2 }}
            >
              {state.error}
            </Alert>
          </Grid2>
        )}

        {/* Timer Display */}
        <Grid2 xs={12}>
          <Paper
            sx={{
              p: 4,
              textAlign: "center",
              background: "linear-gradient(135deg, #282B39 0%, #1a1d2a 100%)",
              border: "1px solid #7895a1",
              borderRadius: 3,
              mb: 1,
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "3rem", sm: "4rem", md: "5rem" },
                fontWeight: 300,
                color:
                  state.timeRemaining <= 60 && state.timeRemaining > 0
                    ? "#ff6b6b"
                    : "#FFD9A8",
                fontFamily: "monospace",
                mb: 2,
                transition: "color 0.3s ease",
              }}
            >
              {formatTimeDisplay(state.timeRemaining)}
            </Typography>

            {/* Progress Bar */}
            {isTimerActive && (
              <Box
                sx={{
                  width: "100%",
                  height: 8,
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  borderRadius: 4,
                  overflow: "hidden",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    width: `${getProgressPercentage()}%`,
                    height: "100%",
                    backgroundColor: "#4CAF50",
                    transition: "width 1s linear",
                  }}
                />
              </Box>
            )}

            {/* Timer Status */}
            {isTimerActive && (
              <Typography
                variant="h6"
                sx={{
                  color: state.isRunning ? "#4CAF50" : "#FF9800",
                  fontWeight: 600,
                }}
              >
                {state.isRunning
                  ? t("countdownTimer.status.running")
                  : t("countdownTimer.status.paused")}
              </Typography>
            )}
          </Paper>
        </Grid2>

        {/* Timer Controls */}
        <Grid2 xs={12}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3, textAlign: "center" }}>
              {t("countdownTimer.controlsTitle")}
            </Typography>

            <Stack
              direction="row"
              spacing={2}
              justifyContent="center"
              flexWrap="wrap"
            >
              {!state.isRunning && !state.isPaused && (
                <CustomButton
                  icon={<PlayArrowIcon sx={{ color: "#FFD9A8" }} />}
                  label={t("countdownTimer.actions.start")}
                  onClick={handleStart}
                  disabled={!hasTimeSet}
                />
              )}

              {state.isRunning && (
                <CustomButton
                  icon={<PauseIcon sx={{ color: "#FFD9A8" }} />}
                  label={t("countdownTimer.actions.pause")}
                  onClick={handlePause}
                />
              )}

              {state.isPaused && (
                <CustomButton
                  icon={<PlayArrowIcon sx={{ color: "#FFD9A8" }} />}
                  label={t("countdownTimer.actions.resume")}
                  onClick={handleResume}
                />
              )}

              {(state.isRunning || state.isPaused) && (
                <CustomButton
                  icon={<StopIcon sx={{ color: "#FFD9A8" }} />}
                  label={t("countdownTimer.actions.stop")}
                  onClick={handleStop}
                />
              )}

              <CustomButton
                icon={<RestartAltIcon sx={{ color: "#FFD9A8" }} />}
                label={t("countdownTimer.actions.reset")}
                onClick={handleReset}
              />
            </Stack>
          </Paper>
        </Grid2>

        {/* Time Input */}
        <Grid2 xs={12} md={6}>
          <Paper
            sx={{
              paddingLeft: 2,
              paddingRight: 2,
              paddingTop: 3,
              paddingBottom: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t("countdownTimer.setTimeTitle")}
            </Typography>

            <Grid2 container spacing={1}>
              <Grid2 xs={4}>
                <TextField
                  fullWidth
                  label={t("countdownTimer.hours")}
                  type="number"
                  value={state.hours}
                  onChange={(e) =>
                    dispatch(hoursChanged(parseInt(e.target.value) || 0))
                  }
                  inputProps={{ min: 0, max: 99 }}
                  disabled={isTimerActive}
                />
              </Grid2>
              <Grid2 xs={4}>
                <TextField
                  fullWidth
                  label={t("countdownTimer.minutes")}
                  type="number"
                  value={state.minutes}
                  onChange={(e) =>
                    dispatch(minutesChanged(parseInt(e.target.value) || 0))
                  }
                  inputProps={{ min: 0, max: 59 }}
                  disabled={isTimerActive}
                />
              </Grid2>
              <Grid2 xs={4}>
                <TextField
                  fullWidth
                  label={t("countdownTimer.seconds")}
                  type="number"
                  value={state.seconds}
                  onChange={(e) =>
                    dispatch(secondsChanged(parseInt(e.target.value) || 0))
                  }
                  inputProps={{ min: 0, max: 59 }}
                  disabled={isTimerActive}
                />
              </Grid2>
            </Grid2>
          </Paper>
        </Grid2>

        {/* Quick Time Presets */}
        <Grid2 xs={12} md={6}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 2,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t("countdownTimer.presetsTitle")}
            </Typography>

            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {quickTimePresets.map((preset) => (
                <Chip
                  key={preset.label as string}
                  label={preset.label}
                  onClick={() =>
                    handleQuickTime(
                      preset.hours,
                      preset.minutes,
                      preset.seconds
                    )
                  }
                  disabled={isTimerActive}
                  sx={{
                    backgroundColor: "#7895a1",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "#5a7a8a",
                    },
                    "&.Mui-disabled": {
                      backgroundColor: "#4a5a6a",
                      color: "#666",
                    },
                  }}
                />
              ))}
            </Stack>
          </Paper>
        </Grid2>

        {/* Settings */}
        <Grid2 xs={12}>
          <Paper
            sx={{
              p: 3,
              background: "#2e303200",
              borderColor: "#282B39",
              borderStyle: "dashed",
              mb: 3,
            }}
          >
            <Typography variant="h4" sx={{ mb: 3 }}>
              {t("countdownTimer.settingsTitle")}
            </Typography>

            <Stack spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={state.showNotification}
                    onChange={(e) =>
                      dispatch(showNotificationChanged(e.target.checked))
                    }
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <NotificationsIcon sx={{ color: "#7895a1" }} />
                    <Typography>
                      {t("countdownTimer.showNotifications")}
                    </Typography>
                  </Box>
                }
              />

              <FormControlLabel
                control={
                  <Switch
                    checked={state.playSound}
                    onChange={(e) =>
                      dispatch(playSoundChanged(e.target.checked))
                    }
                    color="primary"
                  />
                }
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <VolumeUpIcon sx={{ color: "#7895a1" }} />
                    <Typography>{t("countdownTimer.playSound")}</Typography>
                  </Box>
                }
              />
            </Stack>
          </Paper>
        </Grid2>

        <Grid2 xs={12} sx={{ mt: 3, mb: 3 }}>
          <Divider sx={{ backgroundColor: "#7895a1" }}></Divider>
        </Grid2>

        {/* SEO Content */}
        <Grid2 xs={12}>
          <AdSense
                adSlot={ADSENSE_SLOTS.IN_ARTICLE}
                adFormat="fluid"
                adLayoutKey="-fb+5w+4e-db+86"
                sx={{ mb: 3 }}
              />
          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("countdownTimer.content.freeHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("countdownTimer.content.freeDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("countdownTimer.content.easyHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("countdownTimer.content.easyDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("countdownTimer.content.notificationsHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("countdownTimer.content.notificationsDescription")}
          </Typography>

          <Typography variant="h3" sx={{ mb: 2 }}>
            {t("countdownTimer.content.visualHeading")}
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            {t("countdownTimer.content.visualDescription")}
          </Typography>
        </Grid2>
      </Grid2>
    </SideMenuPageContainer>
  );
}
